import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatCents,
  formatPercent,
  formatQuantity,
  PURCHASE_INVOICE_KIND_LABELS,
  PURCHASE_INVOICE_KINDS,
  PURCHASE_INVOICE_STATUS_LABELS,
  taxCodeByCode,
  TAX_CODES,
  type CreatePurchaseInvoiceInput,
  type Paginated,
  type OrderSummary,
  type PurchaseImportResult,
  type PurchaseInvoice,
  type PurchaseInvoiceKind,
  type PurchaseInvoiceSummary,
  type PurchaseSuggestions,
  type ChartOption,
} from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { ContactPicker, type PickedContact } from '../../components/ContactPicker';
import { InfoHint } from '../../components/InfoHint';
import { RecordHistory, type LogLabels } from '../../components/RecordHistory';
import { bpText, centsText, parseBp, parseCents } from '../../money';
import { useSession } from '../../session';
import { InvoicePayments } from './InvoicePayments';
import { invoiceDay } from './InvoicesPage';
import { purchaseBadge } from './PurchaseInvoicesPage';
import { PurchaseDocumentViewer } from './PurchaseDocumentViewer';
import { NewCreditorPanel, type CreditorDefaults } from './NewCreditorPanel';
import { AddDocument, PurchaseImportCard } from './PurchaseImport';
import './invoices.css';
import './purchase.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const euro = (cents: number) => formatCents(cents);

interface GroupRow {
  taxCode: string;
  net: string;
  tax: string;
  /** Die Steuer wurde von Hand geändert und wird nicht mehr aus dem Netto errechnet */
  taxTouched: boolean;
}

interface Form {
  kind: PurchaseInvoiceKind;
  supplier: PickedContact | null;
  /** Lieferant laut Beleg, übernommen aus einem Vorschlag: Er wird mit dem Entwurf gespeichert, damit sich daraus ein Kontakt anlegen lässt */
  supplierSnap: { name: string | null; vatId: string | null; taxNumber: string | null } | null;
  relatedInvoiceId: string;
  number: string;
  invoiceDate: string;
  serviceStart: string;
  serviceEnd: string;
  dueDate: string;
  skontoPercent: string;
  skontoDays: string;
  buyerReference: string;
  iban: string;
  bic: string;
  payReference: string;
  groups: GroupRow[];
  costCenter: string;
  ledgerAccount: string;
  orderId: string;
  deductible: boolean;
  note: string;
}

const emptyGroup = (): GroupRow => ({ taxCode: 'S19', net: '', tax: '', taxTouched: false });
const emptyForm = (): Form => ({
  kind: 'invoice', supplier: null, supplierSnap: null, relatedInvoiceId: '', number: '', invoiceDate: '', serviceStart: '', serviceEnd: '', dueDate: '', skontoPercent: '', skontoDays: '', buyerReference: '',
  iban: '', bic: '', payReference: '', groups: [emptyGroup()], costCenter: '', ledgerAccount: '', orderId: '', deductible: true, note: '',
});

function formOf(i: PurchaseInvoice): Form {
  return {
    kind: i.kind,
    supplier: i.supplierContactId ? { id: i.supplierContactId, name: i.supplierName ?? '' } : null,
    supplierSnap: null,
    relatedInvoiceId: i.relatedInvoiceId ?? '',
    number: i.supplierInvoiceNumber ?? '',
    invoiceDate: i.invoiceDate ?? '',
    serviceStart: i.serviceStart ?? '',
    serviceEnd: i.serviceEnd ?? '',
    dueDate: i.dueDate ?? '',
    skontoPercent: i.discountTermsBp ? bpText(i.discountTermsBp) : '',
    skontoDays: i.discountTermsDays ? String(i.discountTermsDays) : '',
    buyerReference: i.buyerReference ?? '',
    iban: i.payment?.iban ?? '',
    bic: i.payment?.bic ?? '',
    payReference: i.payment?.reference ?? '',
    groups: i.groups.length > 0 ? i.groups.map((g) => ({ taxCode: g.taxCode, net: centsText(g.netCents), tax: centsText(g.taxCents), taxTouched: true })) : [emptyGroup()],
    costCenter: i.costCenter ?? '',
    ledgerAccount: i.ledgerAccount ?? '',
    orderId: i.orderId ?? '',
    deductible: i.inputTaxDeductible,
    note: i.note ?? '',
  };
}

/** Steuer aus dem Nettobetrag, kaufmännisch auf Cent gerundet */
function taxFor(taxCode: string, netCents: number): number {
  const rate = taxCodeByCode(taxCode)?.rate ?? 0;
  const raw = (netCents * rate) / 100;
  return Math.sign(raw) * Math.round(Math.abs(raw));
}

const compact = (s: string) => s.replace(/\s+/g, '').toUpperCase();

/**
 * Maske „Eingangsrechnung erstellen“ und Ansicht einer Eingangsrechnung (KON-142). Ganz oben der Import: Eine E-Rechnung wird gelesen, ihre Felder sind gesperrt, und es
 * bleiben nur die Buchungsangaben frei. Ein einfacher Beleg steht neben den Feldern, die man abtippt. Nach der Freigabe oder Ablehnung ist die Rechnung festgeschrieben.
 */
const euroOf = (d: Record<string, unknown>, key = 'amountCents') => (typeof d[key] === 'number' ? ` über ${centsText(d[key] as number)} €` : '');

/** Sätze für den Verlauf der Eingangsrechnung, aus den Aktionen im Änderungsprotokoll */
const PURCHASE_LOG: LogLabels = {
  create: (d) => (d.origin === 'manual' ? 'Von Hand erfasst' : 'Aus einer Datei eingelesen'),
  create_supplier: (d) => `Lieferant ${d.name ?? ''} angelegt`.trim(),
  update: () => 'Angaben geändert',
  booking: () => 'Kontierung geändert',
  add_document: (d) => `Beleg ${d.fileName ?? ''} hinzugefügt`.replace('  ', ' '),
  remove_document: () => 'Beleg entfernt',
  clear_suggestions: () => 'Vorschläge verworfen',
  approve: (d) => `Freigegeben${euroOf(d, 'grossCents')}`,
  reject: (d) => `Abgelehnt${d.reason ? `: ${d.reason}` : ''}`,
  payment: (d) => `Zahlung${euroOf(d)} erfasst${d.automatic ? ', automatisch' : ''}`,
  payment_reverse: (d) => `Zahlung${euroOf(d)} zurückgenommen`,
  delete: () => 'Gelöscht',
};

export function PurchaseInvoicePage() {
  const { id } = useParams();
  const isCreate = !id;
  const { can, hasModule } = useSession();
  const navigate = useNavigate();
  const client = useQueryClient();

  const existing = useQuery({ queryKey: ['purchase-invoice', id], queryFn: async () => (await api<{ data: PurchaseInvoice }>('GET', `/invoices-in/${id}`)).data, enabled: !isCreate });
  const [form, setForm] = useState<Form>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  // Freigegebene Rechnung: Buchungsangaben (Sachkonto, Kostenstelle, Auftrag, Notiz) nachtragen
  const [editBooking, setEditBooking] = useState(false);
  const loaded = useRef<string | null>(null);

  const invoice = existing.data;
  useEffect(() => {
    if (!invoice) return;
    const key = `${invoice.id}:${invoice.updatedAt}`;
    if (loaded.current === key) return;
    loaded.current = key;
    setForm(formOf(invoice));
  }, [invoice]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }));
  const draft = isCreate || invoice?.status === 'draft';
  const mayWrite = can('invoices_in.write');
  const readOnly = !draft || !mayWrite;
  const locked = !!invoice?.locked;
  const hasLines = (invoice?.lines.length ?? 0) > 0;
  const fixed = readOnly || locked; // gelesene Felder
  const approvedBooking = !draft && invoice?.status === 'approved' && mayWrite; // Buchung lässt sich nachtragen
  const bookable = !readOnly || editBooking; // Buchungsangaben

  const orders = useQuery({
    queryKey: ['orders', 'pick'],
    queryFn: async () => (await api<Paginated<OrderSummary>>('GET', `/orders${queryString({ perPage: 100 })}`)).data,
    enabled: can('orders.read') && (!readOnly || editBooking),
  });
  const related = useQuery({
    queryKey: ['purchase-invoices', 'related', form.supplier?.id],
    queryFn: async () => (await api<Paginated<PurchaseInvoiceSummary>>('GET', `/invoices-in${queryString({ supplierId: form.supplier!.id, status: 'approved', perPage: 100 })}`)).data.filter((r) => r.kind !== 'credit_note'),
    enabled: form.kind === 'credit_note' && !!form.supplier && !readOnly,
  });

  // Das Sachkonto kommt aus dem Kontenplan, wenn es das Modul gibt und die Rolle ihn lesen darf. Aufwandskonten stehen zuerst.
  const chartOptions = useQuery({
    queryKey: ['chart-options'],
    queryFn: async () => (await api<{ data: ChartOption[] }>('GET', '/chart/options')).data,
    enabled: hasModule('chart') && can('chart.read') && (!readOnly || editBooking),
    staleTime: 60_000,
  });
  const accountOptions = (chartOptions.data ?? []).filter((o) => o.kind === 'expense' || o.kind === 'asset' || o.kind === 'neutral' || o.kind === 'equity').sort((a, b) => Number(b.kind === 'expense') - Number(a.kind === 'expense'));

  const totals = useMemo(() => {
    if (hasLines) return { net: invoice!.netCents, tax: invoice!.taxCents, gross: invoice!.grossCents };
    let net = 0;
    let tax = 0;
    for (const g of form.groups) {
      net += parseCents(g.net) ?? 0;
      tax += parseCents(g.tax) ?? 0;
    }
    return { net, tax, gross: net + tax };
  }, [form.groups, hasLines, invoice]);

  function body(): Record<string, unknown> {
    const common = {
      supplierContactId: form.supplier?.id ?? null,
      costCenter: form.costCenter.trim(),
      ledgerAccount: form.ledgerAccount.trim() || null,
      orderId: form.orderId || null,
      inputTaxDeductible: form.deductible,
      note: form.note.trim(),
    };
    if (locked) return common;
    const skBp = form.skontoPercent.trim() ? parseBp(form.skontoPercent) : null;
    const skDays = form.skontoDays.trim() ? Number(form.skontoDays.trim()) : null;
    const iban = compact(form.iban);
    const bic = compact(form.bic);
    const rows = form.groups.filter((g) => g.net.trim() || g.tax.trim());
    return {
      ...common,
      ...(form.supplierSnap && (form.supplierSnap.name || form.supplierSnap.vatId)
        ? { supplier: { name: form.supplierSnap.name ?? form.supplierSnap.vatId, vatId: form.supplierSnap.vatId, taxNumber: form.supplierSnap.taxNumber } }
        : {}),
      kind: form.kind,
      relatedInvoiceId: form.kind === 'credit_note' ? form.relatedInvoiceId || null : null,
      supplierInvoiceNumber: form.number.trim(),
      invoiceDate: form.invoiceDate || null,
      serviceStart: form.serviceStart || null,
      serviceEnd: form.serviceEnd || null,
      dueDate: form.dueDate || null,
      buyerReference: form.buyerReference.trim(),
      discountTermsBp: skBp != null && skDays != null ? skBp : null,
      discountTermsDays: skBp != null && skDays != null ? skDays : null,
      payment: iban || bic || form.payReference.trim() ? { iban: iban || null, bic: bic || null, reference: form.payReference.trim() || null } : null,
      ...(hasLines ? {} : { groups: rows.map((g) => ({ taxCode: g.taxCode, netCents: parseCents(g.net) ?? 0, taxCents: parseCents(g.tax) ?? taxFor(g.taxCode, parseCents(g.net) ?? 0) })) }),
    };
  }

  const onError = (e: unknown, fallback: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (e instanceof ApiError && e.status === 422) {
      setErrors(e.fields);
      setError('Bitte prüfen Sie die markierten Felder.');
    } else setError(errorText(e, fallback));
  };
  const accept = (res: PurchaseInvoice) => {
    client.setQueryData(['purchase-invoice', res.id], res);
    client.invalidateQueries({ queryKey: ['purchase-invoices'] });
    client.invalidateQueries({ queryKey: ['purchase-open-balance'] });
    setError(null);
    setErrors({});
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = body();
      if (isCreate) {
        // Beim Anlegen heißt „kein Datum“: weglassen. Erst beim Ändern löscht null einen Wert.
        const create = Object.fromEntries(Object.entries(payload).filter(([k, v]) => !(['invoiceDate', 'serviceStart', 'serviceEnd', 'dueDate'].includes(k) && v === null)));
        return (await api<{ data: PurchaseInvoice }>('POST', '/invoices-in', create as unknown as CreatePurchaseInvoiceInput)).data;
      }
      return (await api<{ data: PurchaseInvoice }>('PATCH', `/invoices-in/${id}`, payload)).data;
    },
    onSuccess: (res) => {
      accept(res);
      if (isCreate) navigate(`/eingangsrechnungen/${res.id}`, { replace: true });
    },
    onError: (e) => onError(e, 'Die Eingangsrechnung konnte nicht gespeichert werden.'),
  });
  const saveBooking = useMutation({
    mutationFn: async () => (await api<{ data: PurchaseInvoice }>('PATCH', `/invoices-in/${id}/booking`, { costCenter: form.costCenter.trim(), ledgerAccount: form.ledgerAccount.trim() || null, orderId: form.orderId || null, note: form.note.trim() })).data,
    onSuccess: (res) => {
      accept(res);
      client.invalidateQueries({ queryKey: ['chart-totals'] });
      setEditBooking(false);
    },
    onError: (e) => onError(e, 'Die Buchungsangaben konnten nicht gespeichert werden.'),
  });
  const approve = useMutation({
    mutationFn: async () => {
      // Erst speichern, was auf dem Bildschirm steht, damit die Freigabe nicht den alten Stand festschreibt
      if (!readOnly) await api('PATCH', `/invoices-in/${id}`, body());
      return (await api<{ data: PurchaseInvoice }>('POST', `/invoices-in/${id}/approve`)).data;
    },
    onSuccess: accept,
    onError: (e) => {
      client.invalidateQueries({ queryKey: ['purchase-invoice', id] });
      onError(e, 'Die Eingangsrechnung konnte nicht freigegeben werden.');
    },
  });
  const reject = useMutation({
    mutationFn: async () => (await api<{ data: PurchaseInvoice }>('POST', `/invoices-in/${id}/reject`, { reason: reason.trim() })).data,
    onSuccess: (res) => {
      accept(res);
      setRejecting(false);
      setReason('');
    },
    onError: (e) => onError(e, 'Die Eingangsrechnung konnte nicht abgelehnt werden.'),
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/invoices-in/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['purchase-invoices'] });
      client.invalidateQueries({ queryKey: ['purchase-open-balance'] });
      client.removeQueries({ queryKey: ['purchase-invoice', id] });
      navigate('/eingangsrechnungen', { replace: true });
    },
    onError: (e) => onError(e, 'Der Entwurf konnte nicht gelöscht werden.'),
  });
  const removeDoc = useMutation({
    mutationFn: async (docId: string) => (await api<{ data: PurchaseInvoice }>('DELETE', `/invoices-in/${id}/documents/${docId}`)).data,
    onSuccess: accept,
    onError: (e) => onError(e, 'Der Beleg konnte nicht entfernt werden.'),
  });

  const clearSuggestions = useMutation({
    mutationFn: async () => (await api<{ data: PurchaseInvoice }>('DELETE', `/invoices-in/${id}/suggestions`)).data,
    onSuccess: accept,
    onError: (e) => onError(e, 'Die Vorschläge konnten nicht verworfen werden.'),
  });

  function onImported(result: PurchaseImportResult) {
    client.setQueryData(['purchase-invoice', result.invoice.id], result.invoice);
    client.invalidateQueries({ queryKey: ['purchase-invoices'] });
    navigate(`/eingangsrechnungen/${result.invoice.id}`);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setErrors({});
    save.mutate();
  }

  function setGroup(index: number, patch: Partial<GroupRow>) {
    setForm((f) => ({
      ...f,
      groups: f.groups.map((g, i) => {
        if (i !== index) return g;
        const next = { ...g, ...patch };
        // Solange die Steuer nicht von Hand geändert wurde, folgt sie dem Nettobetrag und dem Steuerschlüssel
        if (!next.taxTouched && ('net' in patch || 'taxCode' in patch)) {
          const net = parseCents(next.net);
          next.tax = net === null ? '' : centsText(taxFor(next.taxCode, net));
        }
        return next;
      }),
    }));
  }

  /** Was der Vorschlag setzen würde, je Zeile: Beschriftung, Anzeige, ob es schon so im Formular steht, und das Übernehmen */
  const suggestionRows = suggestionRowsOf(invoice?.suggestions ?? null, form, setForm);
  /** Vorbelegung für „Neuen Kreditor anlegen“: was die Rechnung über den Lieferanten weiß, sonst was schon im Formular steht */
  const known = invoice?.supplier ?? null;
  const fromSuggestion = invoice?.suggestions?.supplier ?? null;
  const creditorDefaults: CreditorDefaults = {
    name: form.supplierSnap?.name ?? known?.name ?? fromSuggestion?.name ?? '',
    street: known?.street ?? '',
    postalCode: known?.postalCode ?? '',
    city: known?.city ?? '',
    email: known?.email ?? '',
    vatId: form.supplierSnap?.vatId ?? known?.vatId ?? fromSuggestion?.vatId ?? '',
    iban: form.iban || invoice?.payment?.iban || invoice?.suggestions?.iban || '',
    bic: form.bic || invoice?.payment?.bic || invoice?.suggestions?.bic || '',
  };
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  if (!isCreate && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && (existing.isError || !invoice)) {
    return (
      <div className="card narrow">
        <h1>Eingangsrechnung nicht gefunden</h1>
        <Link to="/eingangsrechnungen">Zur Liste</Link>
      </div>
    );
  }

  const badge = invoice ? purchaseBadge({ status: invoice.status, paymentStatus: invoice.settlement?.status ?? null, overdue: invoice.settlement?.overdueDays != null }) : null;
  const profile = invoice?.einvoice?.profile;
  const orderOptions = orders.data ?? [];

  return (
    <>
      <p><Link to="/eingangsrechnungen" className="back-link">← Alle Eingangsrechnungen</Link></p>
      <div className="page-head">
        <h1>{isCreate ? 'Eingangsrechnung erstellen' : (invoice!.supplierInvoiceNumber ?? 'Eingangsrechnung')}</h1>
        {badge && <span className={badge.className}>{badge.text}</span>}
        {invoice && <span className="muted">{PURCHASE_INVOICE_KIND_LABELS[invoice.kind]}{invoice.supplierName ? ` · ${invoice.supplierName}` : ''}</span>}
      </div>
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {isCreate && can('invoices_in.write') && <PurchaseImportCard onImported={onImported} />}

      {invoice?.alreadyPaid && draft && (
        <div className="alert alert--ok" role="status">
          <strong>Schon bezahlt.</strong> Der Lieferant hat diese Rechnung als beglichen gekennzeichnet ({formatCents(invoice.alreadyPaid.amountCents)}
          {invoice.alreadyPaid.paidOn ? `, am ${new Date(`${invoice.alreadyPaid.paidOn}T12:00:00`).toLocaleDateString('de-DE')}` : ''}
          {invoice.alreadyPaid.reference ? `, Referenz ${invoice.alreadyPaid.reference}` : ''}). Mit der Freigabe verbucht Wirkia die Zahlung. Sie müssen keine Zahlung erfassen.
        </div>
      )}
      {invoice && invoice.origin === 'einvoice' && draft && (
        <div className="alert alert--ok" role="status">
          E-Rechnung erkannt ({invoice.einvoice?.syntax === 'ubl' ? 'UBL' : 'CII'}, Profil {profile === 'xrechnung' ? 'XRechnung' : profile === 'en16931' ? 'EN 16931' : (profile ?? 'unbekannt').toUpperCase()}): Wirkia hat die Felder{invoice.lines.length ? ` und ${invoice.lines.length} Position${invoice.lines.length === 1 ? '' : 'en'}` : ''} gelesen. Sie sind gesperrt, es bleiben die Buchungsangaben. Stimmt etwas nicht, lehnen Sie die Rechnung ab und fordern beim Lieferanten eine korrigierte an.
        </div>
      )}
      {invoice && invoice.origin === 'document' && draft && (
        <div className="alert" role="status">Kein E-Rechnungsformat: Wirkia hat den Beleg angehängt und liest nichts daraus. Bitte erfassen Sie die Felder von Hand, der Beleg steht daneben.</div>
      )}
      {invoice && invoice.warnings.length > 0 && (
        <div className="alert alert--warn" role="alert">
          <strong>Bitte prüfen:</strong>
          <ul className="purchase-warnings">{invoice.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
        </div>
      )}
      {invoice && invoice.status === 'approved' && <div className="alert alert--ok">Freigegeben{invoice.approvedAt ? ` am ${stamp.format(new Date(invoice.approvedAt))} Uhr` : ''}{invoice.approvedByName ? ` von ${invoice.approvedByName}` : ''}. Die Rechnung ist festgeschrieben und lässt sich nicht mehr ändern. Korrigiert wird mit einer Gutschrift des Lieferanten.</div>}
      {invoice && invoice.status === 'rejected' && <div className="alert alert--error">Abgelehnt{invoice.rejectedAt ? ` am ${stamp.format(new Date(invoice.rejectedAt))} Uhr` : ''}{invoice.rejectedByName ? ` von ${invoice.rejectedByName}` : ''}: {invoice.rejectReason}</div>}
      {invoice?.relatedInvoiceId && <div className="alert">Bezieht sich auf die Rechnung <Link to={`/eingangsrechnungen/${invoice.relatedInvoiceId}`}>{invoice.relatedInvoiceNumber}</Link>. Der Betrag mindert deren offenen Betrag.</div>}

      {invoice && draft && !readOnly && invoice.suggestions && suggestionRows.length > 0 && (
        <section className="card suggestions" aria-labelledby="suggestions-title">
          <h2 id="suggestions-title">Vorschläge aus dem Beleg</h2>
          <p className="muted small">Wirkia hat den Text der PDF gelesen und folgende Angaben gefunden. Das sind Vorschläge, kein Ergebnis: Sie ändern nichts, bis Sie sie übernehmen und speichern. Prüfen Sie jeden Wert am Beleg.</p>
          <p className={`suggestions__sums ${invoice.suggestions.amountsConfirmed ? 'is-ok' : 'is-warn'}`} role="status">
            {invoice.suggestions.groups
              ? invoice.suggestions.amountsConfirmed
                ? `Beträge nachgerechnet: Netto plus Steuer ergibt ${euro(invoice.suggestions.grossCents ?? 0)}, und dieser Betrag steht im Beleg.`
                : 'Die Beträge konnte Wirkia nicht nachrechnen. Bitte prüfen Sie sie besonders genau.'
              : 'Beträge hat Wirkia keine sicher gefunden. Bitte tragen Sie sie selbst ein.'}
          </p>
          <ul className="suggestions__list">
            {suggestionRows.map((r) => (
              <li key={r.key} className="suggestions__row">
                <span className="suggestions__label">{r.label}</span>
                <span className="suggestions__value">{r.text}</span>
                {r.applied ? <span className="muted small">übernommen</span> : <button type="button" className="btn btn--secondary btn--sm" onClick={r.apply} aria-label={`${r.label} übernehmen`}>Übernehmen</button>}
              </li>
            ))}
          </ul>
          <div className="form-actions">
            <button type="button" className="btn btn--primary btn--sm" disabled={suggestionRows.every((r) => r.applied)} onClick={() => suggestionRows.forEach((r) => !r.applied && r.apply())}>Alle übernehmen</button>
            <button type="button" className="btn btn--secondary btn--sm" disabled={clearSuggestions.isPending} onClick={() => clearSuggestions.mutate()}>Verwerfen</button>
          </div>
        </section>
      )}

      <div className={`purchase-grid${invoice && invoice.documents.length > 0 ? ' has-viewer' : ''}`}>
        <form className="purchase-form" onSubmit={submit} noValidate>
          <section className="card">
            <h2>Rechnung</h2>
            <div className="form-grid">
              <div className="field field--wide">
                <span className="invoice-label">Lieferant<InfoHint label="Lieferant">Der Lieferant kommt aus Ihren Kontakten. Wirkia sucht ihn beim Import über USt-IdNr., IBAN und Name. Gibt es ihn noch nicht, legen Sie ihn aus den Angaben der Rechnung an.</InfoHint></span>
                {readOnly ? (
                  <span>{invoice?.supplierContactId ? <Link to={`/kontakte/${invoice.supplierContactId}`}>{invoice.supplierName}</Link> : (invoice?.supplierName ?? '–')}{invoice?.supplierCreditorNumber ? ` · Kreditor ${invoice.supplierCreditorNumber}` : ''}</span>
                ) : (
                  <ContactPicker value={form.supplier} onChange={(c) => set('supplier', c)} invalid={!!errors.supplierContactId} label="Lieferant" />
                )}
                {err('supplierContactId')}
                {invoice?.supplier && (
                  <small className="muted purchase-supplier">
                    Laut Rechnung: {[invoice.supplier.name, invoice.supplier.street, [invoice.supplier.postalCode, invoice.supplier.city].filter(Boolean).join(' '), invoice.supplier.vatId ? `USt-IdNr. ${invoice.supplier.vatId}` : null].filter(Boolean).join(', ')}
                  </small>
                )}
                {!readOnly && form.supplier && (
                  <small className="muted">
                    {invoice?.supplierContactId === form.supplier.id && invoice.supplierCreditorNumber
                      ? `Kreditor ${invoice.supplierCreditorNumber}`
                      : 'Mit der Freigabe führt Wirkia diesen Lieferanten als Kreditor mit eigener Nummer, falls er noch keiner ist.'}
                  </small>
                )}
                {!readOnly && !form.supplier && (
                  <NewCreditorPanel
                    defaults={creditorDefaults}
                    onCreated={(account) => {
                      set('supplier', { id: account.contactId, name: account.name });
                      setCreated(`Kreditor ${account.number} (${account.name}) ist angelegt und zugeordnet. Speichern Sie die Rechnung, damit es gilt.`);
                    }}
                  />
                )}
                {created && <div className="alert alert--ok" role="status">{created}</div>}
              </div>
              <label className="field">
                <span>Rechnungsnummer des Lieferanten</span>
                <input className="input" value={form.number} onChange={(e) => set('number', e.target.value)} disabled={fixed} maxLength={100} aria-invalid={!!errors.supplierInvoiceNumber} />
                {err('supplierInvoiceNumber')}
              </label>
              <label className="field">
                <span>Art</span>
                <select className="input" value={form.kind} onChange={(e) => set('kind', e.target.value as PurchaseInvoiceKind)} disabled={fixed}>
                  {PURCHASE_INVOICE_KINDS.map((k) => <option key={k} value={k}>{PURCHASE_INVOICE_KIND_LABELS[k]}</option>)}
                </select>
              </label>
              {form.kind === 'credit_note' && (
                <label className="field">
                  <span>Zu Rechnung</span>
                  <select className="input" value={form.relatedInvoiceId} onChange={(e) => set('relatedInvoiceId', e.target.value)} disabled={fixed || !form.supplier} aria-invalid={!!errors.relatedInvoiceId}>
                    <option value="">{form.supplier ? 'Keine Auswahl' : 'Erst den Lieferanten wählen'}</option>
                    {invoice?.relatedInvoiceId && !(related.data ?? []).some((r) => r.id === invoice.relatedInvoiceId) && <option value={invoice.relatedInvoiceId}>{invoice.relatedInvoiceNumber}</option>}
                    {(related.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.supplierInvoiceNumber} · {euro(r.grossCents)}</option>)}
                  </select>
                  {err('relatedInvoiceId')}
                </label>
              )}
              <label className="field">
                <span>Rechnungsdatum</span>
                <input className="input" type="date" value={form.invoiceDate} onChange={(e) => set('invoiceDate', e.target.value)} disabled={fixed} aria-invalid={!!errors.invoiceDate} />
                {err('invoiceDate')}
              </label>
              <label className="field">
                <span>Leistung von</span>
                <input className="input" type="date" value={form.serviceStart} onChange={(e) => set('serviceStart', e.target.value)} disabled={fixed} />
              </label>
              <label className="field">
                <span>Leistung bis</span>
                <input className="input" type="date" value={form.serviceEnd} onChange={(e) => set('serviceEnd', e.target.value)} disabled={fixed} aria-invalid={!!errors.serviceEnd} />
                {err('serviceEnd')}
              </label>
              <label className="field">
                <span>Fällig am</span>
                <input className="input" type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} disabled={fixed} />
              </label>
              <label className="field">
                <span className="invoice-label">Skonto %<InfoHint label="Skonto">Steht auf der Rechnung zum Beispiel „2 % Skonto bei Zahlung in 10 Tagen“, tragen Sie 2 und 10 ein. Beim Bezahlen kann Wirkia dann das Skonto abziehen.</InfoHint></span>
                <input className="input num" inputMode="decimal" value={form.skontoPercent} onChange={(e) => set('skontoPercent', e.target.value)} disabled={fixed} placeholder="2" aria-invalid={!!errors.discountTermsBp} />
                {err('discountTermsBp')}
              </label>
              <label className="field">
                <span>Skonto Tage</span>
                <input className="input num" inputMode="numeric" value={form.skontoDays} onChange={(e) => set('skontoDays', e.target.value)} disabled={fixed} placeholder="10" aria-invalid={!!errors.discountTermsDays} />
                {err('discountTermsDays')}
              </label>
              <label className="field">
                <span>Käuferreferenz oder Bestellnummer</span>
                <input className="input" value={form.buyerReference} onChange={(e) => set('buyerReference', e.target.value)} disabled={fixed} maxLength={100} />
              </label>
            </div>
          </section>

          <section className="card">
            <h2>Beträge</h2>
            {hasLines ? (
              <table className="table table--cards">
                <thead>
                  <tr><th>Pos.</th><th>Bezeichnung</th><th className="num">Menge</th><th className="num">Einzelpreis</th><th className="num">USt.</th><th className="num">Betrag</th></tr>
                </thead>
                <tbody>
                  {invoice!.lines.map((l) => (
                    <tr key={l.id}>
                      <td data-label="Pos.">{l.position}</td>
                      <td><strong>{l.title}</strong>{l.description && <div className="muted small">{l.description}</div>}</td>
                      <td data-label="Menge" className="num">{formatQuantity(l.quantityMilli)} {l.unit}</td>
                      <td data-label="Einzelpreis" className="num">{euro(l.unitPriceCents)}</td>
                      <td data-label="USt." className="num">{l.taxRate > 0 ? `${l.taxRate} %` : (taxCodeByCode(l.taxCode)?.category ?? 'S') === 'S' ? '0 %' : 'frei'}</td>
                      <td data-label="Betrag" className="num">{euro(l.amountCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <>
                <p className="muted small">Tragen Sie die Summen je Steuerschlüssel ein, so wie sie auf der Rechnung stehen. Positionen sind freiwillig, Buchhaltung und Vorsteuer brauchen nur die Summen.</p>
                {form.groups.map((g, index) => (
                  <div key={index} className="purchase-group">
                    <label className="field">
                      <span>Steuerschlüssel</span>
                      <select className="input" value={g.taxCode} onChange={(e) => setGroup(index, { taxCode: e.target.value })} disabled={fixed}>
                        {TAX_CODES.filter((c) => c.code !== 'KU' || g.taxCode === 'KU').map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </select>
                      {err(`groups.${index}.taxCode`)}
                    </label>
                    <label className="field">
                      <span>Netto €</span>
                      <input className="input num" inputMode="decimal" value={g.net} onChange={(e) => setGroup(index, { net: e.target.value })} disabled={fixed} aria-invalid={!!errors[`groups.${index}.netCents`]} />
                      {err(`groups.${index}.netCents`)}
                    </label>
                    <label className="field">
                      <span>Steuer €</span>
                      <input className="input num" inputMode="decimal" value={g.tax} onChange={(e) => setGroup(index, { tax: e.target.value, taxTouched: true })} disabled={fixed} aria-invalid={!!errors[`groups.${index}.taxCents`]} />
                      {err(`groups.${index}.taxCents`)}
                    </label>
                    {!fixed && form.groups.length > 1 && (
                      <button type="button" className="btn btn--secondary btn--sm purchase-group__remove" aria-label={`Zeile ${index + 1} entfernen`} onClick={() => set('groups', form.groups.filter((_, i) => i !== index))}>Entfernen</button>
                    )}
                  </div>
                ))}
                {err('groups')}
                {!fixed && form.groups.length < TAX_CODES.length && <button type="button" className="btn btn--secondary btn--sm" onClick={() => set('groups', [...form.groups, emptyGroup()])}>Weiteren Steuerschlüssel hinzufügen</button>}
              </>
            )}
            <dl className="offer-sum purchase-sum">
              <dt>Netto</dt>
              <dd>{euro(totals.net)}</dd>
              <dt>Steuer</dt>
              <dd>{euro(totals.tax)}</dd>
              <dt className="offer-totals__gross">Gesamt brutto</dt>
              <dd className="offer-totals__gross">{euro(totals.gross)}</dd>
              {invoice && invoice.discountBp > 0 && <><dt>darin Rabatt auf alles</dt><dd>{formatPercent(invoice.discountBp)}</dd></>}
            </dl>
          </section>

          <section className="card">
            <h2>Zahlung</h2>
            <div className="form-grid">
              <label className="field">
                <span>IBAN des Lieferanten</span>
                <input className="input" value={form.iban} onChange={(e) => set('iban', e.target.value)} disabled={fixed} autoComplete="off" aria-invalid={!!errors['payment.iban']} />
                {err('payment.iban')}
              </label>
              <label className="field">
                <span>BIC</span>
                <input className="input" value={form.bic} onChange={(e) => set('bic', e.target.value)} disabled={fixed} autoComplete="off" aria-invalid={!!errors['payment.bic']} />
                {err('payment.bic')}
              </label>
              <label className="field">
                <span>Verwendungszweck</span>
                <input className="input" value={form.payReference} onChange={(e) => set('payReference', e.target.value)} disabled={fixed} maxLength={200} />
              </label>
            </div>
          </section>

          <section className="card">
            <h2>Buchung</h2>
            <div className="form-grid">
              <label className="field">
                <span>Kostenstelle</span>
                <input className="input" value={form.costCenter} onChange={(e) => set('costCenter', e.target.value)} disabled={!bookable} maxLength={50} />
              </label>
              <label className="field">
                <span className="invoice-label">Sachkonto<InfoHint label="Sachkonto">Das Konto, auf das diese Rechnung gebucht wird, nur Ziffern. Ihr Steuerberater nennt es Ihnen. Leer lassen, wenn Sie es nicht wissen.</InfoHint></span>
                {accountOptions.length > 0 ? (
                  <select className="input" value={form.ledgerAccount} onChange={(e) => set('ledgerAccount', e.target.value)} disabled={!bookable} aria-invalid={!!errors.ledgerAccount}>
                    <option value="">Kein Sachkonto</option>
                    {form.ledgerAccount && !accountOptions.some((o) => o.number === form.ledgerAccount) && <option value={form.ledgerAccount}>{form.ledgerAccount} (nicht im Kontenplan)</option>}
                    {accountOptions.map((o) => <option key={o.number} value={o.number}>{o.number} {o.name}</option>)}
                  </select>
                ) : (
                  <input className="input num" inputMode="numeric" value={form.ledgerAccount} onChange={(e) => set('ledgerAccount', e.target.value)} disabled={!bookable} maxLength={8} aria-invalid={!!errors.ledgerAccount} />
                )}
                {err('ledgerAccount')}
              </label>
              <label className="field">
                <span className="invoice-label">Auftrag<InfoHint label="Auftrag">Ordnen Sie die Kosten einem Auftrag zu, wenn die Rechnung für einen Kunden angefallen ist, zum Beispiel Material. Freiwillig.</InfoHint></span>
                <select className="input" value={form.orderId} onChange={(e) => set('orderId', e.target.value)} disabled={!bookable || !can('orders.read')}>
                  <option value="">Keinem Auftrag zugeordnet</option>
                  {invoice?.orderId && !orderOptions.some((o) => o.id === invoice.orderId) && <option value={invoice.orderId}>{invoice.orderNumber}</option>}
                  {orderOptions.map((o) => <option key={o.id} value={o.id}>{o.number} · {o.subject}</option>)}
                </select>
              </label>
              <label className="field field--check">
                <input type="checkbox" checked={form.deductible} onChange={(e) => set('deductible', e.target.checked)} disabled={readOnly} />
                <span className="invoice-label">Vorsteuerabzug möglich<InfoHint label="Vorsteuerabzug">Meist ja. Entfernen Sie das Häkchen, wenn Sie die Steuer dieser Rechnung nicht von Ihrer Umsatzsteuer abziehen dürfen, zum Beispiel bei privater Nutzung. Im Zweifel fragen Sie Ihren Steuerberater.</InfoHint></span>
              </label>
              <label className="field field--wide">
                <span>Notiz</span>
                <textarea className="input textarea textarea--short" value={form.note} onChange={(e) => set('note', e.target.value)} disabled={!bookable} maxLength={2000} />
              </label>
            </div>
            {approvedBooking && (
              <div className="invoice-actions">
                {editBooking ? (
                  <>
                    <button type="button" className="btn btn--primary" disabled={saveBooking.isPending} onClick={() => { setError(null); setErrors({}); saveBooking.mutate(); }}>{saveBooking.isPending ? 'Wird gespeichert …' : 'Buchungsangaben speichern'}</button>
                    <button type="button" className="btn btn--secondary" onClick={() => { if (invoice) setForm(formOf(invoice)); setEditBooking(false); setError(null); setErrors({}); }}>Abbrechen</button>
                  </>
                ) : (
                  <button type="button" className="btn btn--secondary" onClick={() => { setEditBooking(true); setError(null); }}>Buchungsangaben ändern</button>
                )}
                <p className="muted small">Die Rechnung selbst bleibt unverändert. Sachkonto, Kostenstelle, Auftrag und Notiz lassen sich nachtragen, jede Änderung wird protokolliert.</p>
              </div>
            )}
          </section>

          {/* Nur wenn es etwas zu tun gibt: bei einer freigegebenen Rechnung bliebe die Karte sonst leer */}
          {draft && (mayWrite || can('invoices_in.approve') || can('invoices_in.delete')) && (
          <div className="card">
            <div className="invoice-actions">
              {draft && mayWrite && <button type="submit" className="btn btn--primary" disabled={save.isPending || approve.isPending}>{save.isPending ? 'Wird gespeichert …' : isCreate ? 'Entwurf speichern' : 'Speichern'}</button>}
              {invoice && draft && can('invoices_in.approve') && (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={approve.isPending || save.isPending || invoice.approvalBlocked}
                  title={invoice.approvalBlocked ? 'Nach dem Vier-Augen-Prinzip gibt eine andere Person die Rechnung frei.' : undefined}
                  onClick={() => window.confirm('Eingangsrechnung jetzt freigeben? Sie ist danach festgeschrieben und lässt sich nicht mehr ändern. Korrigiert wird mit einer Gutschrift des Lieferanten.') && approve.mutate()}
                >
                  {approve.isPending ? 'Wird freigegeben …' : 'Freigeben'}
                </button>
              )}
              {invoice && draft && can('invoices_in.approve') && <button type="button" className="btn btn--secondary" onClick={() => { setRejecting(!rejecting); setError(null); }}>Ablehnen …</button>}
              {invoice && draft && can('invoices_in.delete') && (
                <button type="button" className="btn btn--danger" disabled={remove.isPending} onClick={() => window.confirm('Diesen Entwurf löschen? Die Datei bleibt im Belegarchiv.') && remove.mutate()}>Entwurf löschen</button>
              )}
              {isCreate && <Link className="btn btn--secondary" to="/eingangsrechnungen">Abbrechen</Link>}
            </div>
            {invoice?.approvalBlocked && <p className="muted small">Vier-Augen-Prinzip: Sie haben diesen Entwurf angelegt, eine andere Person gibt ihn frei.</p>}
            {rejecting && invoice && (
              <form
                className="payment-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!reason.trim()) return setError('Bitte geben Sie einen Grund an.');
                  reject.mutate();
                }}
              >
                <label className="field">
                  <span>Grund der Ablehnung</span>
                  <textarea className="input textarea textarea--short" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
                </label>
                <p className="muted small">Die Rechnung bleibt mit dem Grund stehen und wird nicht bezahlt. Bitten Sie den Lieferanten um eine korrigierte Rechnung.</p>
                <div className="form-actions">
                  <button type="submit" className="btn btn--danger btn--sm" disabled={reject.isPending}>Rechnung ablehnen</button>
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => setRejecting(false)}>Abbrechen</button>
                </div>
              </form>
            )}
          </div>
          )}
        </form>

        {invoice && (
          <aside className="purchase-side" aria-label="Beleg">
            <section className="card">
              <h2>Beleg</h2>
              {draft && mayWrite && <AddDocument invoiceId={invoice.id} onAdded={accept} />}
              <PurchaseDocumentViewer
                invoiceId={invoice.id}
                documents={invoice.documents}
                onRemove={draft && mayWrite ? (doc) => removeDoc.mutate(doc.id) : undefined}
                removable={(doc) => !(invoice.origin === 'einvoice' && doc.role !== 'attachment')}
              />
            </section>
          </aside>
        )}
      </div>

      {invoice && invoice.status === 'approved' && <InvoicePayments invoice={invoice} direction="in" />}
      {invoice && <RecordHistory path={`/invoices-in/${invoice.id}/log`} queryKey={['purchase-log', invoice.id, invoice.updatedAt]} labels={PURCHASE_LOG} storageKey="purchase-log" />}
      {invoice && invoice.status !== 'draft' && (
        <p className="muted small">
          Angelegt {invoiceDay(invoice.createdAt.slice(0, 10))}{invoice.createdByName ? ` von ${invoice.createdByName}` : ''} · {PURCHASE_INVOICE_STATUS_LABELS[invoice.status]}
        </p>
      )}
    </>
  );
}

interface SuggestionRow {
  key: string;
  label: string;
  text: string;
  applied: boolean;
  apply: () => void;
}

const dayText = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;

/** Macht aus den Vorschlägen Zeilen mit Anzeige und „Übernehmen“. Was schon so im Formular steht, gilt als übernommen. */
function suggestionRowsOf(sug: PurchaseSuggestions | null, form: Form, setForm: (update: (f: Form) => Form) => void): SuggestionRow[] {
  if (!sug) return [];
  const rows: SuggestionRow[] = [];
  const put = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));
  const text = (key: 'number' | 'invoiceDate' | 'serviceStart' | 'serviceEnd' | 'dueDate' | 'iban' | 'bic', label: string, value: string | undefined, show = (v: string) => v) => {
    if (value) rows.push({ key, label, text: show(value), applied: form[key] === value, apply: () => put({ [key]: value } as Partial<Form>) });
  };

  if (sug.supplierContact) {
    const c = sug.supplierContact;
    const by = c.matchedBy === 'vat_id' ? 'die USt-IdNr.' : c.matchedBy === 'iban' ? 'die IBAN' : 'den Namen';
    rows.push({ key: 'contact', label: 'Lieferant', text: `${c.name} (aus Ihren Kontakten, gefunden über ${by})`, applied: form.supplier?.id === c.id, apply: () => put({ supplier: { id: c.id, name: c.name } }) });
  } else if (sug.supplier && (sug.supplier.name || sug.supplier.vatId)) {
    const sp = sug.supplier;
    rows.push({
      key: 'supplier',
      label: 'Lieferant laut Beleg',
      text: [sp.name, sp.vatId ? `USt-IdNr. ${sp.vatId}` : null, sp.taxNumber ? `Steuernr. ${sp.taxNumber}` : null].filter(Boolean).join(', ') + ' (noch nicht in Ihren Kontakten)',
      applied: form.supplierSnap?.name === sp.name && form.supplierSnap?.vatId === sp.vatId,
      apply: () => put({ supplierSnap: sp }),
    });
  }
  text('number', 'Rechnungsnummer', sug.supplierInvoiceNumber);
  text('invoiceDate', 'Rechnungsdatum', sug.invoiceDate, dayText);
  text('serviceStart', 'Leistung von', sug.serviceStart, dayText);
  text('serviceEnd', 'Leistung bis', sug.serviceEnd, dayText);
  text('dueDate', 'Fällig am', sug.dueDate, dayText);
  if (sug.discountTermsBp && sug.discountTermsDays) {
    const percent = bpText(sug.discountTermsBp);
    const days = String(sug.discountTermsDays);
    rows.push({ key: 'skonto', label: 'Skonto', text: `${percent} % bei Zahlung in ${days} Tagen`, applied: form.skontoPercent === percent && form.skontoDays === days, apply: () => put({ skontoPercent: percent, skontoDays: days }) });
  }
  text('iban', 'IBAN', sug.iban);
  text('bic', 'BIC', sug.bic);
  if (sug.groups && sug.groups.length > 0) {
    const groups: GroupRow[] = sug.groups.map((g) => ({ taxCode: g.taxCode, net: centsText(g.netCents), tax: centsText(g.taxCents), taxTouched: true }));
    const same = form.groups.length === groups.length && groups.every((g, i) => form.groups[i]!.taxCode === g.taxCode && form.groups[i]!.net === g.net && form.groups[i]!.tax === g.tax);
    rows.push({
      key: 'groups',
      label: 'Beträge',
      text: sug.groups.map((g) => `${taxCodeByCode(g.taxCode)?.label ?? g.taxCode}: netto ${euro(g.netCents)}, Steuer ${euro(g.taxCents)}`).join(' · '),
      applied: same,
      apply: () => put({ groups }),
    });
  }
  return rows;
}
